import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useParams } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import AiAskBox from '../../components/AiAskBox/AiAskBox';
import FeaturePromo from '../../components/FeaturePromo/FeaturePromo';
import { askBoxProps } from '../../data/languagePages';
import { chartHref } from '../../data/chartLanguage';
import {
  CARDS,
  HUB_PATHS,
  MEANING_LANGS,
  cardPath,
  findCardByRouteSlug,
  loadContent,
} from '../../data/cardMeanings';
import STRINGS from './strings';
import { ARTICLE_BY_SLUG, LEARN_TAROT_ENABLED } from '../LearnTarot/learnData';
import './CardMeanings.css';

const ORIGIN = 'https://nowtarot.com';

const ROMAN = ['0', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX', 'XXI'];

const Paragraphs = ({ text }) =>
  (text || '').split('\n\n').filter(Boolean).map((paragraph, index) => <p key={index}>{paragraph}</p>);

const firstParagraph = (text) => ((text || '').split('\n\n')[0] || '').trim();

const keywordLine = (words) => {
  const picked = (words || []).slice(0, 4);
  if (!picked.length) return '';
  const line = picked.join(', ');
  return line.charAt(0).toUpperCase() + line.slice(1);
};

const Pair = ({ uprightLabel, reversedLabel, uprightText, reversedText }) => (
  <div className="cm-split">
    <div>
      <h3>{uprightLabel}</h3>
      <Paragraphs text={uprightText} />
    </div>
    <div>
      <h3>{reversedLabel}</h3>
      <Paragraphs text={reversedText} />
    </div>
  </div>
);

const LEARN_GUIDES = {
  major: [
    ['major-vs-minor-arcana', 'Learn about the Major Arcana'],
    ['upright-vs-reversed-tarot-cards', 'Learn how reversals work'],
    ['how-to-interpret-tarot-card-combinations', 'Learn how to read multiple cards together'],
  ],
  minor: [
    ['major-vs-minor-arcana', 'How the four suits and court cards work'],
    ['upright-vs-reversed-tarot-cards', 'Learn how reversals work'],
    ['how-to-interpret-tarot-card-combinations', 'Learn how to read multiple cards together'],
  ],
};

function LearnGuides({ arcana }) {
  const guides = LEARN_GUIDES[arcana === 'major' ? 'major' : 'minor'].filter(([slug]) => ARTICLE_BY_SLUG[slug]);
  if (!guides.length) return null;
  return (
    <section className="cm-section cm-learn-guides">
      <h2>Keep learning</h2>
      <ul>
        {guides.map(([slug, label]) => (
          <li key={slug}><Link to={ARTICLE_BY_SLUG[slug].path}>{label} →</Link></li>
        ))}
      </ul>
    </section>
  );
}

const CardMeaningPage = ({ lang = 'en' }) => {
  const { slug } = useParams();
  const strings = STRINGS[lang];
  const card = findCardByRouteSlug(slug, lang);
  const [content, setContent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    loadContent(lang).then((data) => {
      if (!cancelled) setContent(data);
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  if (!card) {
    return <Navigate to={HUB_PATHS[lang]} replace />;
  }

  const name = card.names[lang];
  const ask = askBoxProps(lang);
  const canonical = `${ORIGIN}${cardPath(card, lang)}`;
  const cardContent = content && content[card.slug];
  const summary = cardContent ? firstParagraph(cardContent.upright.general) : '';
  const metaDescription = summary
    ? (summary.length <= 180 ? summary : `${summary.slice(0, 177).replace(/\s+\S*$/, '')}…`)
    : strings.cardDescription(name);
  const arcanaLabel = strings.arcana[card.arcana];
  const eyebrow = card.arcana === 'major'
    ? `${ROMAN[card.number] ?? card.number} · ${arcanaLabel}`
    : arcanaLabel;

  const cardIndex = CARDS.indexOf(card);
  const prevCard = CARDS[(cardIndex + CARDS.length - 1) % CARDS.length];
  const nextCard = CARDS[(cardIndex + 1) % CARDS.length];
  const suitCards = CARDS.filter(
    (other) => other.arcana === card.arcana && other.slug !== card.slug
  );

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: strings.cardH1(name),
      description: metaDescription,
      image: `${ORIGIN}${card.image}`,
      inLanguage: lang,
      mainEntityOfPage: canonical,
      author: { '@type': 'Organization', name: 'NowTarot' },
      publisher: { '@type': 'Organization', name: 'NowTarot' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: strings.breadcrumbHome, item: `${ORIGIN}${strings.askPath}` },
        { '@type': 'ListItem', position: 2, name: strings.breadcrumbHub, item: `${ORIGIN}${HUB_PATHS[lang]}` },
        { '@type': 'ListItem', position: 3, name, item: canonical },
      ],
    },
  ];

  return (
    <div className="card-meanings-page" lang={lang} dir={strings.dir}>
      <Helmet>
        <html lang={lang} />
        <title>{strings.cardTitle(name)}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="x-default" href={`${ORIGIN}${cardPath(card, 'en')}`} />
        {MEANING_LANGS.map((code) => (
          <link key={code} rel="alternate" hrefLang={code} href={`${ORIGIN}${cardPath(card, code)}`} />
        ))}
        <meta property="og:title" content={strings.cardTitle(name)} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={`${ORIGIN}${card.image}`} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={strings.cardTitle(name)} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={`${ORIGIN}${card.image}`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />

      <main className="cm-container">
        <nav className="cm-breadcrumbs" aria-label="Breadcrumb">
          <Link to={strings.askPath}>{strings.breadcrumbHome}</Link>
          <span aria-hidden="true"> › </span>
          <Link to={HUB_PATHS[lang]}>{strings.breadcrumbHub}</Link>
          <span aria-hidden="true"> › </span>
          <span>{name}</span>
        </nav>

        <nav className="cm-language-switcher" aria-label="Language switcher">
          <span className="cm-language-label">{strings.languageSwitcherLabel}</span>
          <div className="cm-language-links">
            {MEANING_LANGS.map((switchLang) => (
              <Link
                key={switchLang}
                to={cardPath(card, switchLang)}
                className={`cm-language-link ${switchLang === lang ? 'is-active' : ''}`}
              >
                {strings.languageNames[switchLang]}
              </Link>
            ))}
          </div>
        </nav>

        <header className="cm-hero">
          <img
            src={card.image}
            alt={`${name} — ${arcanaLabel}`}
            className="cm-hero-image"
            width="300"
            height="527"
          />
          <div className="cm-hero-text">
            <p className="cm-arcana-label">{eyebrow}</p>
            <h1>{strings.cardH1(name)}</h1>
            {cardContent && (
              <>
                <p className="cm-lead-keywords">{keywordLine(cardContent.keywords.upright)}</p>
                <p className="cm-lead">{summary}</p>
              </>
            )}
            <div className="cm-verdicts">
              <span className={`cm-verdict cm-verdict-${card.polarity.upright}`}>
                {strings.upright}: {strings.polarity[card.polarity.upright]}
              </span>
              <span className={`cm-verdict cm-verdict-${card.polarity.reversed}`}>
                {strings.reversed}: {strings.polarity[card.polarity.reversed]}
              </span>
            </div>
            {cardContent && (
              <div className="cm-keyword-lines">
                <p><strong>{strings.upright}:</strong> {cardContent.keywords.upright.join(', ')}</p>
                <p><strong>{strings.reversed}:</strong> {cardContent.keywords.reversed.join(', ')}</p>
              </div>
            )}
          </div>
        </header>

        <section className="cm-own-spread">
          <p className="cm-own-spread-title">
            <span className="cm-own-spread-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <rect x="8" y="2.5" width="11" height="15" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                <rect className="cm-own-spread-card" x="4" y="6.5" width="11" height="15" rx="1.5" />
              </svg>
            </span>
            {strings.ownSpreadTitle(name)}
          </p>
          <p>{strings.ownSpreadText}</p>
          <div className="cm-own-spread-foot">
            <span className="cm-own-spread-note">{strings.ownSpreadNote}</span>
            <Link to={chartHref('/tarot-card-interpreter/', lang, { card: card.slug })}>
              {strings.ownSpreadCta} <span className="cm-own-spread-arrow" aria-hidden="true">→</span>
            </Link>
          </div>
        </section>

        {cardContent && (
          <nav className="cm-jump" aria-label={strings.onThisPage}>
            <a href="#upright">{strings.upright}</a>
            <a href="#reversed">{strings.reversed}</a>
            <a href="#love">{strings.jumpLove}</a>
            <a href="#career">{strings.jumpCareer}</a>
            <a href="#yes-no">{strings.jumpYesNo}</a>
            <a href="#symbolism">{strings.jumpSymbolism}</a>
          </nav>
        )}

        {cardContent && (
          <>
            <section id="upright" className="cm-section cm-meaning cm-meaning-upright">
              <h2>{strings.uprightHeading(name)}</h2>
              <Paragraphs text={cardContent.upright.general} />
            </section>

            <section id="reversed" className="cm-section cm-meaning cm-meaning-reversed">
              <h2>{strings.reversedHeading(name)}</h2>
              <Paragraphs text={cardContent.reversed.general} />
            </section>

            <section className="cm-cta cm-cta-compact">
              <h2>{strings.askCompactHeading(name)}</h2>
              <p>{strings.askCompactText}</p>
              <AiAskBox {...ask} />
            </section>

            <section id="love" className="cm-section">
              <h2>{strings.love}</h2>
              <Pair
                uprightLabel={strings.upright}
                reversedLabel={strings.reversed}
                uprightText={cardContent.upright.love}
                reversedText={cardContent.reversed.love}
              />
            </section>

            <section id="career" className="cm-section">
              <h2>{strings.careerMoney}</h2>
              <h3 className="cm-subhead">{strings.career}</h3>
              <Pair
                uprightLabel={strings.upright}
                reversedLabel={strings.reversed}
                uprightText={cardContent.upright.career}
                reversedText={cardContent.reversed.career}
              />
              <h3 className="cm-subhead">{strings.finances}</h3>
              <Pair
                uprightLabel={strings.upright}
                reversedLabel={strings.reversed}
                uprightText={cardContent.upright.finances}
                reversedText={cardContent.reversed.finances}
              />
            </section>

            <section id="yes-no" className="cm-section cm-yesno">
              <h2>{strings.yesNoHeading(name)}</h2>
              <p>
                <span className={`cm-verdict cm-verdict-${card.polarity.upright}`}>
                  {strings.upright}: {strings.polarity[card.polarity.upright]}
                </span>{' '}
                {cardContent.yesNo.upright}
              </p>
              <p>
                <span className={`cm-verdict cm-verdict-${card.polarity.reversed}`}>
                  {strings.reversed}: {strings.polarity[card.polarity.reversed]}
                </span>{' '}
                {cardContent.yesNo.reversed}
              </p>
              <p>
                <Link to={strings.yesNoPath} className="cm-inline-link">
                  {strings.yesNoCta} →
                </Link>
              </p>
            </section>

            <section id="symbolism" className="cm-section">
              <h2>{strings.descriptionHeading(name)}</h2>
              <Paragraphs text={cardContent.description} />
            </section>
          </>
        )}

        <section className="cm-cta cm-cta-dark">
          <h2>{strings.askCtaHeadingForCard(name)}</h2>
          <p>{strings.askCtaText}</p>
          <AiAskBox {...ask} />
          <p>
            <Link to={strings.drawPath} className="cm-inline-link">
              {strings.drawCta} →
            </Link>
          </p>
        </section>

        <nav className="cm-prev-next" aria-label="Card navigation">
          <Link to={cardPath(prevCard, lang)} className="cm-nav-link">
            <img src={prevCard.image} alt="" width="44" height="77" loading="lazy" />
            <span>
              <small>← {strings.prevCard}</small>
              {prevCard.names[lang]}
            </span>
          </Link>
          <Link to={cardPath(nextCard, lang)} className="cm-nav-link cm-nav-next">
            <span>
              <small>{strings.nextCard} →</small>
              {nextCard.names[lang]}
            </span>
            <img src={nextCard.image} alt="" width="44" height="77" loading="lazy" />
          </Link>
        </nav>

        <section className="cm-section cm-related">
          <h2>{strings.moreInArcana(strings.arcana[card.arcana])}</h2>
          <ul className="cm-related-list">
            {suitCards.map((other) => (
              <li key={other.slug}>
                <Link to={cardPath(other, lang)}>
                  <img src={other.image} alt="" width="80" height="140" loading="lazy" />
                  <span>{other.names[lang]}</span>
                </Link>
              </li>
            ))}
          </ul>
          <p>
            <Link to={HUB_PATHS[lang]} className="cm-inline-link">
              {strings.allCards} →
            </Link>
          </p>
        </section>

        {lang === 'en' && LEARN_TAROT_ENABLED && <LearnGuides arcana={card.arcana} />}

        <FeaturePromo variant="page" lang={lang} seed={card.slug} />
      </main>

      <Footer />
    </div>
  );
};

export default CardMeaningPage;
